import React, { useState, useEffect } from "react";
import axios from "axios";
import { ENQUIRY_TYPES, BRAND } from "../data/content";
import { ArrowUpRight, CheckCircle2, Linkedin, Mail, MapPin, Lock, Phone } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useTurnstile, TurnstileFallback } from "../hooks/useTurnstile";
import { useDocumentMeta } from "../hooks/useDocumentMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  full_name: "",
  email: "",
  organisation: "",
  role: "",
  phone: "",
  country: "",
  enquiry_type: "general",
  timeframe: "",
  message: "",
  consent: true,
};

export default function Contact() {
  useDocumentMeta({
    title: "Contact · Let's build something that lasts — JJ Argent",
    description:
      "Send a role brief, consulting enquiry, speaking invitation or partnership idea. Response within two business days.",
    path: "/contact",
  });
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const { containerRef: turnstileRef, token: turnstileToken, reset: resetTurnstile, scriptFailed: turnstileFailed } = useTurnstile();

  // Batch 2 §10A — About Speaking block links here with ?type=speaking so the
  // enquiry-type dropdown is pre-set to "Speaking or workshop".
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const t = searchParams.get("type");
    if (t && ENQUIRY_TYPES.some((e) => e.value === t)) {
      setForm((f) => ({ ...f, enquiry_type: t }));
    }
  }, [searchParams]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      toast.error("Please confirm consent to submit your enquiry.");
      return;
    }
    if (!turnstileToken) {
      toast.error("Please complete the bot check");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/enquiries`, { ...form, turnstileToken });
      setDone(true);
      toast.success("Enquiry received — I'll respond within two business days.");
      resetTurnstile();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map((d) => d.msg).join(", ") : detail || err?.message;
      toast.error(`Couldn't send: ${msg || "please try again"}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main id="main" className="bg-[#0B0B0C] text-[#FAF9F6]" data-testid="contact-page">
      <section className="pt-40 pb-16 relative grain">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <p className="eyebrow">Contact</p>
          <h1 className="mt-6 font-display text-5xl md:text-6xl lg:text-7xl leading-[0.98] tracking-[-0.04em] max-w-4xl">
            Let's build something{" "}<br />
            <span className="text-[#D8A936]">that outlasts all of us.</span>
          </h1>
        </div>
      </section>

      <section className="border-t border-[#343437] pb-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 pt-16">
          {/* Left: info */}
          <aside className="lg:col-span-4 space-y-8">
            <div>
              <p className="eyebrow">Direct</p>
              <ul className="mt-5 space-y-4 text-[15px]">
                <li className="flex items-start gap-3">
                  <Mail size={18} className="text-[#D8A936] mt-0.5" />
                  <a href={`mailto:${BRAND.email}`} className="hover:text-[#D8A936] transition-colors" data-testid="contact-email-link">
                    {BRAND.email}
                  </a>
                </li>
                <li className="flex items-start gap-3">
                  <Phone size={18} className="text-[#D8A936] mt-0.5" />
                  <a href={BRAND.phoneHref} className="hover:text-[#D8A936] transition-colors" data-testid="contact-phone-link">
                    {BRAND.phone}
                  </a>
                </li>
                <li className="flex items-start gap-3">
                  <Linkedin size={18} className="text-[#D8A936] mt-0.5" />
                  <a href={BRAND.linkedin} target="_blank" rel="noopener noreferrer" className="hover:text-[#D8A936] transition-colors" data-testid="contact-linkedin">
                    LinkedIn
                  </a>
                </li>
                <li className="flex items-start gap-3">
                  <MapPin size={18} className="text-[#D8A936] mt-0.5" />
                  <span>{BRAND.location}</span>
                </li>
              </ul>
            </div>

            <div className="border border-[#343437] p-6 bg-[#151517]">
              <p className="eyebrow">Response time</p>
              <p className="mt-3 text-[#FAF9F6]">
                I read every enquiry personally and respond within <span className="text-[#D8A936]">two business days</span>.
              </p>
            </div>

            <div>
              <p className="eyebrow">Best for</p>
              <ul className="mt-4 space-y-2 text-[#A9A9A6] text-sm">
                <li>· Senior program, project & operations roles</li>
                <li>· Consulting & interim leadership</li>
                <li>· Speaking, panels & workshops</li>
                <li>· University AI readiness pilots</li>
                <li>· Partnerships & media</li>
              </ul>
            </div>
          </aside>

          {/* Right: form */}
          <div className="lg:col-span-8">
            {done ? (
              <div
                data-testid="contact-success"
                className="border border-[#D8A936] p-10 md:p-14 bg-[#151517] flex flex-col items-start gap-6"
              >
                <CheckCircle2 size={40} className="text-[#D8A936]" />
                <h2 className="font-display text-3xl md:text-4xl leading-tight tracking-tight">
                  Thanks — your enquiry is in.
                </h2>
                <p className="text-[#A9A9A6] max-w-lg">
                  I'll be in touch within two business days. In the meantime, feel free to connect on LinkedIn or
                  explore the case studies.
                </p>
                <div className="w-full border-t border-[#343437] pt-6 mt-2">
                  <p className="eyebrow mb-3">While you wait</p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <Link
                      to="/diagnostic"
                      data-testid="contact-success-diagnostic"
                      className="block border border-[#343437] p-4 hover:border-[#D8A936] transition-colors group"
                    >
                      <p className="font-display text-xs tracking-[0.14em] uppercase text-[#D8A936] group-hover:text-[#FAF9F6]">
                        Free diagnostic
                      </p>
                      <p className="mt-2 text-sm text-[#A9A9A6] leading-relaxed">
                        Score your program's resilience in 2 minutes.
                      </p>
                    </Link>
                    <Link
                      to="/case-studies"
                      data-testid="contact-success-case-studies"
                      className="block border border-[#343437] p-4 hover:border-[#D8A936] transition-colors group"
                    >
                      <p className="font-display text-xs tracking-[0.14em] uppercase text-[#D8A936] group-hover:text-[#FAF9F6]">
                        Case studies
                      </p>
                      <p className="mt-2 text-sm text-[#A9A9A6] leading-relaxed">
                        See the receipts behind the numbers.
                      </p>
                    </Link>
                    <Link
                      to="/services"
                      data-testid="contact-success-services"
                      className="block border border-[#343437] p-4 hover:border-[#D8A936] transition-colors group"
                    >
                      <p className="font-display text-xs tracking-[0.14em] uppercase text-[#D8A936] group-hover:text-[#FAF9F6]">
                        Services
                      </p>
                      <p className="mt-2 text-sm text-[#A9A9A6] leading-relaxed">
                        How I engage — audit, interim, advisory.
                      </p>
                    </Link>
                  </div>
                </div>
                <div className="flex flex-wrap gap-3">
                  <a href={BRAND.linkedin} target="_blank" rel="noopener noreferrer" className="btn-primary">
                    Connect on LinkedIn <ArrowUpRight size={14} />
                  </a>
                  <button
                    type="button"
                    onClick={() => { setForm(initial); setDone(false); }}
                    data-testid="contact-send-another"
                    className="btn-ghost"
                  >
                    Send another
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-8" data-testid="contact-form">
                <div
                  className="flex items-start gap-3 border border-[#343437] bg-[#151517]/60 px-4 py-3"
                  data-testid="contact-privacy-note"
                >
                  <Lock size={14} className="text-[#D8A936] mt-1 shrink-0" aria-hidden />
                  <p className="text-xs leading-relaxed text-[#A9A9A6]">
                    Your details are used solely to reply to this enquiry. No newsletter, no sharing.
                    Delete on request at any time. See the <Link to="/privacy" className="underline text-[#D8A936]">privacy policy</Link>.
                  </p>
                </div>

                <Field label="Full name" required>
                  <input
                    type="text"
                    required
                    value={form.full_name}
                    onChange={(e) => update("full_name", e.target.value)}
                    className="input"
                    data-testid="contact-full-name"
                  />
                </Field>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Field label="Email" required>
                    <input
                      type="email"
                      required
                      value={form.email}
                      onChange={(e) => update("email", e.target.value)}
                      className="input"
                      data-testid="contact-email-input"
                    />
                  </Field>
                  <Field label="Phone (optional)">
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={(e) => update("phone", e.target.value)}
                      className="input"
                      data-testid="contact-phone"
                    />
                  </Field>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Field label="Organisation">
                    <input
                      type="text"
                      value={form.organisation}
                      onChange={(e) => update("organisation", e.target.value)}
                      className="input"
                      data-testid="contact-organisation"
                    />
                  </Field>
                  <Field label="Your role">
                    <input
                      type="text"
                      value={form.role}
                      onChange={(e) => update("role", e.target.value)}
                      className="input"
                      data-testid="contact-role"
                    />
                  </Field>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Field label="Country">
                    <input
                      type="text"
                      value={form.country}
                      onChange={(e) => update("country", e.target.value)}
                      className="input"
                      placeholder="e.g. Australia"
                      data-testid="contact-country"
                    />
                  </Field>
                  <Field label="Timeframe">
                    <input
                      type="text"
                      value={form.timeframe}
                      onChange={(e) => update("timeframe", e.target.value)}
                      className="input"
                      placeholder="e.g. Q2 2026"
                      data-testid="contact-timeframe"
                    />
                  </Field>
                </div>

                <Field label="What brings you here?" required>
                  <select
                    value={form.enquiry_type}
                    onChange={(e) => update("enquiry_type", e.target.value)}
                    className="input"
                    data-testid="contact-enquiry-type"
                  >
                    {ENQUIRY_TYPES.map((t) => (
                      <option key={t.value} value={t.value} className="bg-[#0B0B0C]">
                        {t.label}
                      </option>
                    ))}
                  </select>
                </Field>

                <Field label="Tell me more" required>
                  <textarea
                    required
                    rows={6}
                    value={form.message}
                    onChange={(e) => update("message", e.target.value)}
                    className="input resize-none"
                    placeholder="Give me a snapshot of the project, opportunity or challenge."
                    data-testid="contact-message"
                  />
                </Field>

                <label className="flex items-start gap-3 text-sm text-[#A9A9A6]">
                  <input
                    type="checkbox"
                    checked={form.consent}
                    onChange={(e) => update("consent", e.target.checked)}
                    className="mt-1 accent-[#D8A936]"
                    data-testid="contact-consent"
                  />
                  <span>
                    I consent to JJ Argent storing my details to respond to this enquiry. No newsletter,
                    no sharing — just a reply.
                  </span>
                </label>

                <div ref={turnstileRef} data-testid="contact-turnstile" className="my-4" />
                <TurnstileFallback show={turnstileFailed} className="-mt-2 mb-2" />

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit"
                  className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? "Sending…" : "Send enquiry"} <ArrowUpRight size={14} />
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      <style>{`
        .input {
          width: 100%;
          background: transparent;
          border: 1px solid #343437;
          color: #FAF9F6;
          padding: 0.95rem 1rem;
          font-family: 'Manrope', sans-serif;
          font-size: 0.95rem;
          transition: border-color 300ms ease;
        }
        .input:focus {
          border-color: #D8A936;
          outline: none;
        }
        .input::placeholder { color: #6a6a6a; }
        select.input option { color: #FAF9F6; background: #0B0B0C; }
      `}</style>
    </main>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block eyebrow mb-3">
        {label} {required && <span className="text-[#D8A936]">*</span>}
      </span>
      {children}
    </label>
  );
}
